import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import {
  Loader2, KeyRound, Save, Eye, EyeOff, ShieldCheck, CreditCard, Users, DollarSign,
  RefreshCcw, Search, Download, CheckCircle2, Clock, XCircle, X, ExternalLink, Copy, BarChart3, Ban, Play,
  Mail, AlertTriangle, Bell, Send, ShieldAlert, FileText, Building2, Receipt, CircleDollarSign, ToggleLeft, ToggleRight,
  Handshake, Trash2, Sparkles, Volume2, Upload, RotateCcw,
} from "lucide-react";

const inp =
  "w-full rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2.5 font-body text-[#1F2937] outline-none focus:border-[#0D9488]";

function money(cents, currency = "usd") {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format((cents || 0) / 100);
  } catch {
    return `$${((cents || 0) / 100).toFixed(2)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function StatusPill({ paid, status }) {
  const label = (status || "").toLowerCase();
  if (paid || label === "paid" || label === "completed") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-2 py-0.5 text-xs font-bold text-[#047857]" data-testid="payment-status-paid"><CheckCircle2 size={12} /> Paid</span>;
  }
  if (label === "pending" || label === "initiated" || label === "") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-xs font-bold text-[#B45309]" data-testid="payment-status-pending"><Clock size={12} /> Pending</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-[#EF4444]/15 px-2 py-0.5 text-xs font-bold text-[#B91C1C]" data-testid="payment-status-failed"><XCircle size={12} /> {status || "Failed"}</span>;
}

function StatTile({ icon: Icon, label, value, testId, tint = "#0D9488" }) {
  return (
    <div className="rounded-2xl border border-[#E5DECF] bg-white p-4" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl text-white" style={{ background: tint }}>
          <Icon size={18} />
        </div>
        <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">{label}</div>
      </div>
      <div className="mt-2 font-heading text-2xl font-extrabold text-[#1F2937]">{value}</div>
    </div>
  );
}

function toCsv(rows) {
  const headers = ["date", "subscriber", "email", "role", "plan", "interval", "amount", "currency", "status", "session_id"];
  const esc = (s) => {
    if (s == null) return "";
    const v = String(s);
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  const body = rows.map((t) => [
    t.created_at, t.user_name || "", t.user_email || "", t.user_role || "",
    t.plan_name || t.plan || t.lookup_key || "", t.interval || "",
    ((t.amount_cents || 0) / 100).toFixed(2), (t.currency || "usd").toUpperCase(),
    t.paid ? "paid" : (t.payment_status || t.status || ""), t.session_id || "",
  ].map(esc).join(","));
  return [headers.join(","), ...body].join("\n");
}

// Palette used by the revenue chart + drawer plan chips (stable per plan slug).
const PLAN_COLORS = ["#0D9488", "#7A3FF2", "#FF9E1B", "#0284C7", "#10B981", "#EF4444", "#F59E0B", "#3B82F6", "#EC4899", "#14B8A6"];
function colorForPlan(plan, index) {
  return PLAN_COLORS[index % PLAN_COLORS.length];
}

function RevenueByPlanChart({ transactions }) {
  // Build a { "YYYY-MM": { plan_name: cents, ... } } map from paid transactions
  // covering the trailing 6 months (including current). Client-side so the
  // chart tracks whatever data the panel already shows.
  const chart = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString(undefined, { month: "short" }), year: d.getFullYear() });
    }
    const monthIndex = Object.fromEntries(months.map((m, i) => [m.key, i]));
    const planTotals = {};
    const perMonth = months.map(() => ({}));
    for (const t of transactions || []) {
      if (!t.paid || !t.created_at) continue;
      const d = new Date(t.created_at);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!(key in monthIndex)) continue;
      const plan = t.plan_name || t.plan || "Unknown";
      const cents = t.amount_cents || 0;
      perMonth[monthIndex[key]][plan] = (perMonth[monthIndex[key]][plan] || 0) + cents;
      planTotals[plan] = (planTotals[plan] || 0) + cents;
    }
    // Order plans by total revenue desc so the biggest earner sits at the bottom.
    const plans = Object.entries(planTotals).sort((a, b) => b[1] - a[1]).map(([name], i) => ({ name, color: colorForPlan(name, i) }));
    const maxMonth = Math.max(1, ...perMonth.map((m) => Object.values(m).reduce((s, v) => s + v, 0)));
    return { months, plans, perMonth, maxMonth, totalPaid: Object.values(planTotals).reduce((s, v) => s + v, 0) };
  }, [transactions]);

  const empty = chart.totalPaid === 0;
  return (
    <div className="mt-5 rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-5" data-testid="revenue-chart">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0D9488]/10 text-[#0D9488]"><BarChart3 size={18} /></div>
          <div>
            <div className="font-heading text-sm font-bold text-[#1F2937]">Revenue by plan · last 6 months</div>
            <div className="font-body text-xs text-[#6B7280]">Paid transactions only, stacked by plan.</div>
          </div>
        </div>
        <div className="font-heading text-lg font-extrabold text-[#0D9488]">{money(chart.totalPaid)}</div>
      </div>

      {empty ? (
        <div className="mt-4 rounded-xl border border-dashed border-[#E5DECF] bg-white px-4 py-6 text-center font-body text-sm text-[#6B7280]" data-testid="revenue-chart-empty">
          No paid revenue in the last 6 months yet — paid transactions will appear here.
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-6 gap-3">
            {chart.months.map((m, i) => {
              const stacks = chart.perMonth[i];
              const monthTotal = Object.values(stacks).reduce((s, v) => s + v, 0);
              const heightPct = (monthTotal / chart.maxMonth) * 100;
              return (
                <div key={m.key} className="flex flex-col items-center" data-testid={`revenue-bar-${m.key}`}>
                  <div className="relative flex h-40 w-full items-end justify-center">
                    <div className="relative flex w-8 flex-col-reverse overflow-hidden rounded-lg bg-white ring-1 ring-[#E5DECF]" style={{ height: `${Math.max(heightPct, monthTotal > 0 ? 4 : 0)}%`, minHeight: monthTotal > 0 ? 6 : 0 }}>
                      {chart.plans.map((p) => {
                        const cents = stacks[p.name] || 0;
                        if (!cents) return null;
                        const share = (cents / monthTotal) * 100;
                        return (
                          <div key={p.name} title={`${p.name}: ${money(cents)}`} data-testid={`revenue-bar-${m.key}-${p.name}`}
                            style={{ height: `${share}%`, background: p.color }} />
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-2 font-heading text-xs font-bold text-[#1F2937]">{m.label}</div>
                  <div className="font-mono text-[10px] text-[#6B7280]">{monthTotal ? money(monthTotal) : "—"}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1">
            {chart.plans.map((p) => (
              <div key={p.name} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F2937]" data-testid={`revenue-legend-${p.name}`}>
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />
                {p.name}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DrawerRow({ label, value, testId, mono = false }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-[#F2ECE1] py-2 last:border-b-0" data-testid={testId}>
      <span className="text-xs uppercase tracking-wider font-bold text-[#6B7280]">{label}</span>
      <span className={`text-sm text-[#1F2937] ${mono ? "font-mono" : "font-body"} break-all`}>{value}</span>
    </div>
  );
}

function PaymentDetailDrawer({ open, sessionId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    setLoading(true); setErr(null); setData(null);
    api.adminPaymentDetail(sessionId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, sessionId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const tx = data?.transaction;
  const s = data?.stripe_session;
  const sub = data?.subscriber;

  return (
    <div className="fixed inset-0 z-[80] flex" data-testid="payment-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} data-testid="payment-drawer-backdrop" />
      <aside className="ml-auto flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#E5DECF] bg-white px-5 py-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Transaction</div>
            <div className="font-heading text-lg font-extrabold text-[#1F2937]">{tx?.plan_name || tx?.plan || "Payment details"}</div>
          </div>
          <button onClick={onClose} data-testid="payment-drawer-close" className="flex h-9 w-9 items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F2ECE1]"><X size={18} /></button>
        </div>

        {loading && <div className="flex-1 py-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>}
        {err && !loading && <div className="m-5 rounded-xl bg-[#EF4444]/10 px-4 py-3 text-sm font-semibold text-[#B91C1C]" data-testid="payment-drawer-error">{err}</div>}

        {data && !loading && (
          <div className="px-5 py-4 space-y-6">
            {/* Amount + status hero */}
            <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4">
              <div className="flex items-baseline justify-between">
                <div className="font-heading text-3xl font-extrabold text-[#1F2937]" data-testid="drawer-amount">{money(tx?.amount_cents, tx?.currency)}</div>
                <StatusPill paid={tx?.paid} status={tx?.payment_status || tx?.status} />
              </div>
              <div className="mt-1 font-body text-xs text-[#6B7280]">{fmtDate(tx?.created_at)} · {tx?.interval || "—"}</div>
            </div>

            {/* Subscriber */}
            <section data-testid="drawer-subscriber">
              <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">Subscriber</div>
              {sub ? (
                <div className="mt-2 rounded-2xl border border-[#E5DECF] p-4">
                  <div className="font-heading font-bold text-[#1F2937]">{sub.name || "—"}</div>
                  <div className="font-body text-sm text-[#6B7280]">{sub.email}{sub.role ? ` · ${sub.role}` : ""}</div>
                  <div className="mt-2 font-mono text-xs text-[#6B7280]">User id: {sub.id}</div>
                </div>
              ) : <div className="mt-2 font-body text-sm text-[#6B7280]">Subscriber record not found (may be deleted).</div>}
            </section>

            {/* Stripe session */}
            <section data-testid="drawer-stripe-session">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">Stripe session</div>
                {data.receipt_url && (
                  <a href={data.receipt_url} target="_blank" rel="noreferrer" data-testid="drawer-receipt-link"
                    className="inline-flex items-center gap-1 rounded-full bg-[#0D9488] px-3 py-1 text-xs font-bold text-white hover:bg-[#0B7F74]">
                    <ExternalLink size={12} /> Receipt
                  </a>
                )}
              </div>
              <div className="mt-2 rounded-2xl border border-[#E5DECF] p-4">
                {data.stripe_error && (
                  <div className="mb-2 rounded-lg bg-[#F59E0B]/15 px-3 py-2 text-xs font-semibold text-[#B45309]" data-testid="drawer-stripe-error">
                    Live Stripe lookup unavailable: {data.stripe_error}
                  </div>
                )}
                <DrawerRow label="Session id" value={
                  <span className="inline-flex items-center gap-1">
                    {tx?.session_id}
                    <button onClick={() => copyToClipboard(tx?.session_id, "Session id copied")} data-testid="drawer-copy-session" className="text-[#6B7280] hover:text-[#0D9488]"><Copy size={12} /></button>
                  </span>
                } testId="drawer-session-id" />
                <DrawerRow label="Mode" value={s?.mode} testId="drawer-mode" />
                <DrawerRow label="Status" value={s?.status} testId="drawer-status" />
                <DrawerRow label="Payment status" value={s?.payment_status} testId="drawer-payment-status" />
                <DrawerRow label="Amount total" value={s ? money(s.amount_total, s.currency || tx?.currency) : null} testId="drawer-amount-total" />
                <DrawerRow label="Customer" value={[s?.customer_name, s?.customer_email].filter(Boolean).join(" · ")} testId="drawer-customer" />
                <DrawerRow label="Customer id" value={s?.customer_id} testId="drawer-customer-id" mono />
                <DrawerRow label="Subscription id" value={s?.subscription_id || tx?.stripe_subscription_id} testId="drawer-subscription-id" mono />
                <DrawerRow label="Payment intent" value={s?.payment_intent_id} testId="drawer-pi-id" mono />
                <DrawerRow label="Card" value={s?.card_brand ? `${s.card_brand} ···· ${s.card_last4}` : null} testId="drawer-card" />
                <DrawerRow label="Plan lookup key" value={tx?.lookup_key} testId="drawer-lookup-key" mono />
              </div>
            </section>

            {/* Other payments */}
            <section data-testid="drawer-other-payments">
              <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">Other payments from this subscriber ({(data.other_payments || []).length})</div>
              {(data.other_payments || []).length === 0 ? (
                <div className="mt-2 font-body text-sm text-[#6B7280]" data-testid="drawer-others-empty">No other payments on file.</div>
              ) : (
                <ul className="mt-2 divide-y divide-[#F2ECE1] rounded-2xl border border-[#E5DECF]">
                  {data.other_payments.map((o) => (
                    <li key={o.session_id} className="flex items-center justify-between px-3 py-2.5" data-testid={`drawer-other-${o.session_id}`}>
                      <div className="min-w-0 pr-2">
                        <div className="font-heading text-sm font-bold text-[#1F2937] truncate">{o.plan_name || o.plan || "—"}</div>
                        <div className="font-body text-xs text-[#6B7280]">{fmtDate(o.created_at)} · {o.interval || "one-time"}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="font-mono text-sm font-bold text-[#1F2937]">{money(o.amount_cents, o.currency)}</div>
                        <StatusPill paid={o.paid} status={o.payment_status || o.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}

function PaymentsPanel() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ transactions: [], totals: {}, active_subscribers: [] });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tab, setTab] = useState("transactions");
  const [detailSession, setDetailSession] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // subscriber row awaiting confirm
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = async () => {
    try {
      const d = await api.adminPayments();
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doResume = async (userId) => {
    try {
      await api.adminResumeSubscription(userId);
      toast.success("Subscription resumed — will renew normally");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doCancel = async (immediate) => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    try {
      const r = await api.adminCancelSubscription(cancelTarget.user_id, immediate);
      if (r.stripe_error) {
        toast.warning(`Marked ${immediate ? "canceled" : "cancel at period end"} locally, but Stripe reported: ${r.stripe_error}`);
      } else {
        toast.success(immediate ? "Subscription canceled immediately" : "Subscription will end at the current period");
      }
      setCancelTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelBusy(false);
    }
  };

  const filteredTx = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data.transactions || []).filter((t) => {
      if (statusFilter === "paid" && !t.paid) return false;
      if (statusFilter === "pending" && (t.paid || !["pending", "initiated", ""].includes((t.payment_status || t.status || "").toLowerCase()))) return false;
      if (statusFilter === "failed" && (t.paid || ["pending", "initiated", ""].includes((t.payment_status || t.status || "").toLowerCase()))) return false;
      if (!ql) return true;
      const hay = `${t.user_name || ""} ${t.user_email || ""} ${t.plan_name || ""} ${t.plan || ""} ${t.session_id || ""}`.toLowerCase();
      return hay.includes(ql);
    });
  }, [data.transactions, q, statusFilter]);

  const filteredSubs = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data.active_subscribers || []).filter((s) => {
      if (!ql) return true;
      const hay = `${s.user_name || ""} ${s.user_email || ""} ${s.plan_name || ""} ${s.plan || ""}`.toLowerCase();
      return hay.includes(ql);
    });
  }, [data.active_subscribers, q]);

  const totals = data.totals || {};
  const downloadCsv = () => {
    const csv = toCsv(filteredTx);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `zoodleap-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-8 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="admin-payments-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><CreditCard size={20} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-[#1F2937]">Payments & Subscribers</h2>
            <p className="font-body text-xs text-[#6B7280]">All Stripe transactions across the platform and every active subscription.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setRefreshing(true); load(); }} data-testid="payments-refresh" className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1.5 text-sm font-bold hover:border-[#0D9488]">
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />} Refresh
          </button>
          <button onClick={downloadCsv} data-testid="payments-export-csv" className="inline-flex items-center gap-2 rounded-full bg-[#1F2937] px-3 py-1.5 text-sm font-bold text-white hover:bg-[#111827]">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile icon={DollarSign} label="Revenue collected" value={money(totals.paid_gross_cents)} testId="stat-revenue" tint="#0D9488" />
            <StatTile icon={CheckCircle2} label="Paid transactions" value={totals.paid_count ?? 0} testId="stat-paid" tint="#10B981" />
            <StatTile icon={Clock} label="Pending" value={totals.pending_count ?? 0} testId="stat-pending" tint="#F59E0B" />
            <StatTile icon={Users} label="Active subscribers" value={(data.active_subscribers || []).length} testId="stat-active-subs" tint="#7A3FF2" />
          </div>

          <RevenueByPlanChart transactions={data.transactions || []} />

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border border-[#E5DECF] bg-[#FAF7F2] p-1">
              {[
                ["transactions", `Transactions (${(data.transactions || []).length})`],
                ["subscribers", `Active subscribers (${(data.active_subscribers || []).length})`],
              ].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)} data-testid={`payments-tab-${id}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${tab === id ? "bg-white shadow text-[#1F2937]" : "text-[#6B7280] hover:text-[#1F2937]"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="relative ml-auto flex items-center gap-2">
              {tab === "transactions" && (
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="payments-status-filter"
                  className="rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1.5 text-xs font-bold text-[#1F2937] focus:border-[#0D9488]">
                  <option value="all">All statuses</option>
                  <option value="paid">Paid only</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              )}
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="payments-search"
                  placeholder="Search name, email or plan…"
                  className="w-64 rounded-full border-2 border-[#E5DECF] bg-white pl-8 pr-3 py-1.5 text-xs font-semibold text-[#1F2937] focus:border-[#0D9488] outline-none" />
              </div>
            </div>
          </div>

          {tab === "transactions" ? (
            <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E5DECF]" data-testid="payments-transactions-table">
              <table className="min-w-full text-sm">
                <thead className="bg-[#F2ECE1] text-left font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Subscriber</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Interval</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTx.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center font-body text-[#6B7280]" data-testid="payments-empty">No payments yet.</td></tr>
                  )}
                  {filteredTx.map((t) => (
                    <tr key={t.session_id || `${t.user_id}-${t.created_at}`}
                      onClick={() => t.session_id && setDetailSession(t.session_id)}
                      className="cursor-pointer border-t border-[#E5DECF] hover:bg-[#FAF7F2]"
                      data-testid={`payment-row-${t.session_id || "row"}`}>
                      <td className="px-4 py-3 font-body text-xs text-[#6B7280] whitespace-nowrap">{fmtDate(t.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-heading font-bold text-[#1F2937]">{t.user_name || "—"}</div>
                        <div className="font-body text-xs text-[#6B7280]">{t.user_email || t.user_id || ""}{t.user_role ? ` · ${t.user_role}` : ""}</div>
                      </td>
                      <td className="px-4 py-3 font-body">{t.plan_name || t.plan || t.lookup_key || "—"}</td>
                      <td className="px-4 py-3 font-body text-xs text-[#6B7280]">{t.interval || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-[#1F2937]">{money(t.amount_cents, t.currency)}</td>
                      <td className="px-4 py-3"><StatusPill paid={t.paid} status={t.payment_status || t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E5DECF]" data-testid="payments-subscribers-table">
              <table className="min-w-full text-sm">
                <thead className="bg-[#F2ECE1] text-left font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  <tr>
                    <th className="px-4 py-3">Subscriber</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Interval</th>
                    <th className="px-4 py-3">Activated</th>
                    <th className="px-4 py-3">Renewal</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center font-body text-[#6B7280]" data-testid="subscribers-empty">No active subscribers.</td></tr>
                  )}
                  {filteredSubs.map((s) => (
                    <tr key={s.user_id} className="border-t border-[#E5DECF] hover:bg-[#FAF7F2]" data-testid={`subscriber-row-${s.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-heading font-bold text-[#1F2937]">{s.user_name || "—"}</div>
                        <div className="font-body text-xs text-[#6B7280]">{s.user_email}{s.user_role ? ` · ${s.user_role}` : ""}</div>
                      </td>
                      <td className="px-4 py-3 font-body">{s.plan_name || s.plan || "—"}</td>
                      <td className="px-4 py-3 font-body text-xs text-[#6B7280]">{s.interval || "—"}</td>
                      <td className="px-4 py-3 font-body text-xs text-[#6B7280]">{fmtDate(s.activated_at)}</td>
                      <td className="px-4 py-3">
                        {s.cancel_at_period_end
                          ? <span className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-xs font-bold text-[#B45309]">Ends this period</span>
                          : <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-2 py-0.5 text-xs font-bold text-[#047857]">Renewing</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {s.cancel_at_period_end ? (
                          <button onClick={() => doResume(s.user_id)}
                            data-testid={`resume-sub-${s.user_id}`}
                            className="inline-flex items-center gap-1 rounded-full border-2 border-[#10B981] bg-white px-3 py-1 text-xs font-bold text-[#047857] hover:bg-[#10B981]/10">
                            <Play size={12} /> Resume
                          </button>
                        ) : (
                          <button onClick={() => setCancelTarget(s)}
                            data-testid={`cancel-sub-${s.user_id}`}
                            className="inline-flex items-center gap-1 rounded-full border-2 border-[#EF4444]/60 bg-white px-3 py-1 text-xs font-bold text-[#B91C1C] hover:bg-[#EF4444]/10">
                            <Ban size={12} /> Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <PaymentDetailDrawer open={!!detailSession} sessionId={detailSession} onClose={() => setDetailSession(null)} />
      {cancelTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4" data-testid="cancel-confirm-modal">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#EF4444]/10 text-[#B91C1C]"><Ban size={20} /></div>
              <div>
                <h3 className="font-heading text-lg font-extrabold text-[#1F2937]">Cancel subscription</h3>
                <p className="font-body text-xs text-[#6B7280]">{cancelTarget.user_name || cancelTarget.user_email} · {cancelTarget.plan_name || cancelTarget.plan}</p>
              </div>
            </div>
            <p className="mt-4 font-body text-sm text-[#1F2937]">
              How should this subscription end?
            </p>
            <ul className="mt-2 space-y-1 font-body text-xs text-[#6B7280]">
              <li><strong className="text-[#1F2937]">End of period</strong> — access continues until the paid period ends, then no auto-renew (recommended).</li>
              <li><strong className="text-[#1F2937]">Cancel now</strong> — plan turns off immediately, no refund is issued.</li>
            </ul>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button onClick={() => setCancelTarget(null)} disabled={cancelBusy}
                data-testid="cancel-confirm-close"
                className="inline-flex items-center gap-1 rounded-full border-2 border-[#E5DECF] bg-white px-4 py-2 text-sm font-bold text-[#1F2937] hover:border-[#1F2937]">
                Keep active
              </button>
              <button onClick={() => doCancel(false)} disabled={cancelBusy}
                data-testid="cancel-confirm-eop"
                className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B] px-4 py-2 text-sm font-bold text-[#1F2937] hover:brightness-95 disabled:opacity-50">
                {cancelBusy ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />} End of period
              </button>
              <button onClick={() => doCancel(true)} disabled={cancelBusy}
                data-testid="cancel-confirm-immediate"
                className="inline-flex items-center gap-1 rounded-full bg-[#EF4444] px-4 py-2 text-sm font-bold text-white hover:brightness-95 disabled:opacity-50">
                {cancelBusy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} Cancel now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentAlertsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [data, setData] = useState({ settings: null, fallback_recipients: [], failures: [] });
  const [form, setForm] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const d = await api.adminGetPaymentAlerts();
      setData(d);
      const s = d.settings;
      setForm({
        enabled: s.enabled,
        subject_prefix: s.subject_prefix,
        smtp: { ...s.smtp, password: "" }, // never populate password from server
      });
      setRecipientsText((s.recipients || []).join(", "));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setSmtp = (k, v) => setForm((f) => ({ ...f, smtp: { ...f.smtp, [k]: v } }));

  const parsedRecipients = useMemo(
    () => recipientsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter((s) => s.includes("@")),
    [recipientsText]
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.adminSavePaymentAlerts({
        enabled: form.enabled,
        recipients: parsedRecipients,
        subject_prefix: form.subject_prefix,
        smtp: {
          ...form.smtp,
          password: form.smtp.password ? form.smtp.password : null, // null keeps existing
        },
      });
      toast.success("Payment alerts saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const to = testTo.trim() || undefined;
      const r = await api.adminTestPaymentAlert(to);
      toast.success(`Test alert sent to ${r.sent_to}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  const resolve = async (id, resolved) => {
    try {
      await api.adminResolveFailure(id, resolved);
      toast.success(resolved ? "Marked resolved" : "Reopened");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !form) return (
    <div className="mt-8 rounded-3xl border border-[#E5DECF] bg-white p-8 text-center" data-testid="alerts-loading">
      <Loader2 className="mx-auto animate-spin text-[#0D9488]" />
    </div>
  );

  const smtpReady = form.smtp.host && form.smtp.from_email;
  const openFailures = (data.failures || []).filter((f) => !f.resolved).length;

  return (
    <div className="mt-8 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="payment-alerts-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#EF4444]/10 text-[#B91C1C]"><Bell size={20} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-[#1F2937]">Failed-payment alerts</h2>
            <p className="font-body text-xs text-[#6B7280]">Email the platform admin every time a subscription payment fails — catch churn early.</p>
          </div>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-[#E5DECF] bg-[#FAF7F2] px-3 py-1.5" data-testid="alerts-enable-toggle">
          <input type="checkbox" className="h-4 w-4 accent-[#0D9488]" checked={!!form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
          <span className="text-sm font-bold">{form.enabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      {openFailures > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#EF4444]/10 px-3 py-2 text-sm font-semibold text-[#B91C1C]" data-testid="alerts-open-count">
          <AlertTriangle size={16} /> {openFailures} unresolved payment failure{openFailures > 1 ? "s" : ""}
        </div>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div>
          <label className="block text-xs uppercase tracking-widest font-bold text-[#6B7280]">Recipients</label>
          <textarea value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)}
            data-testid="alerts-recipients"
            className={`${inp} mt-1 h-24`}
            placeholder="ops@yourcompany.com, billing@yourcompany.com" />
          <p className="mt-1 font-body text-xs text-[#6B7280]">Comma, space or newline separated. {parsedRecipients.length ? `${parsedRecipients.length} recipient${parsedRecipients.length > 1 ? "s" : ""}.` : `Falls back to admin accounts (${(data.fallback_recipients || []).join(", ") || "none"}).`}</p>

          <label className="mt-4 block text-xs uppercase tracking-widest font-bold text-[#6B7280]">Subject prefix</label>
          <input value={form.subject_prefix} onChange={(e) => setForm((f) => ({ ...f, subject_prefix: e.target.value }))}
            data-testid="alerts-subject-prefix"
            className={`${inp} mt-1`} placeholder="[ZoodLeap Billing]" />
        </div>

        <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[#0D9488]" />
            <div className="font-heading text-sm font-bold text-[#1F2937]">SMTP delivery</div>
            {smtpReady && <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold text-[#047857]">Configured</span>}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="col-span-2 text-xs font-bold">Host<input value={form.smtp.host} onChange={(e) => setSmtp("host", e.target.value)} data-testid="alerts-smtp-host" className={`${inp} mt-1`} placeholder="smtp.sendgrid.net" /></label>
            <label className="text-xs font-bold">Port<input type="number" value={form.smtp.port} onChange={(e) => setSmtp("port", parseInt(e.target.value || "587", 10))} data-testid="alerts-smtp-port" className={`${inp} mt-1`} /></label>
            <label className="text-xs font-bold flex flex-col justify-end">
              <span className="mb-1">Security</span>
              <select value={form.smtp.use_ssl ? "ssl" : (form.smtp.use_tls ? "tls" : "none")}
                onChange={(e) => { const v = e.target.value; setSmtp("use_ssl", v === "ssl"); setSmtp("use_tls", v === "tls"); }}
                data-testid="alerts-smtp-security" className={inp}>
                <option value="tls">STARTTLS</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select>
            </label>
            <label className="col-span-2 text-xs font-bold">Username<input value={form.smtp.username} onChange={(e) => setSmtp("username", e.target.value)} data-testid="alerts-smtp-username" className={`${inp} mt-1`} placeholder="apikey" autoComplete="off" /></label>
            <label className="col-span-2 text-xs font-bold">Password{form.smtp.password_set && !form.smtp.password && <span className="ml-2 font-normal text-[#6B7280]">(saved — leave blank to keep)</span>}
              <div className="relative mt-1">
                <input type={showPassword ? "text" : "password"} value={form.smtp.password || ""} onChange={(e) => setSmtp("password", e.target.value)}
                  data-testid="alerts-smtp-password" className={`${inp} pr-11`} autoComplete="new-password" placeholder={form.smtp.password_set ? "•••••••••••••••" : ""} />
                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0D9488]" aria-label="Toggle password visibility">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
            <label className="text-xs font-bold">From email<input value={form.smtp.from_email} onChange={(e) => setSmtp("from_email", e.target.value)} data-testid="alerts-smtp-from-email" className={`${inp} mt-1`} placeholder="alerts@zoodleap.com" /></label>
            <label className="text-xs font-bold">From name<input value={form.smtp.from_name} onChange={(e) => setSmtp("from_name", e.target.value)} data-testid="alerts-smtp-from-name" className={`${inp} mt-1`} placeholder="ZoodLeap Alerts" /></label>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={saving} data-testid="alerts-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF9E1B] px-5 py-2.5 font-heading font-bold text-[#1F2937] hover:brightness-95 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save alerts
        </button>
        <div className="flex items-center gap-2">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="alerts-test-to"
            className={`${inp} w-56`} placeholder="test@you.com (optional)" />
          <button onClick={sendTest} disabled={testing || !smtpReady} data-testid="alerts-send-test"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#0D9488] bg-white px-4 py-2 font-bold text-[#0D9488] hover:bg-[#0D9488]/10 disabled:opacity-50">
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-[#B91C1C]" />
          <div className="font-heading text-sm font-bold text-[#1F2937]">Recent failures ({(data.failures || []).length})</div>
        </div>
        <div className="mt-2 overflow-x-auto rounded-2xl border border-[#E5DECF]" data-testid="failures-table">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F2ECE1] text-left font-heading text-xs uppercase tracking-widest text-[#6B7280]">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Subscriber</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Attempt</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(data.failures || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center font-body text-[#6B7280]" data-testid="failures-empty">No payment failures logged yet.</td></tr>
              )}
              {(data.failures || []).map((f) => (
                <tr key={f.id} className={`border-t border-[#E5DECF] ${f.resolved ? "opacity-60" : ""}`} data-testid={`failure-row-${f.id}`}>
                  <td className="px-4 py-3 font-body text-xs text-[#6B7280] whitespace-nowrap">{fmtDate(f.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-heading font-bold text-[#1F2937]">{f.user_name || "—"}</div>
                    <div className="font-body text-xs text-[#6B7280]">{f.user_email || f.customer_id}{f.user_role ? ` · ${f.user_role}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 font-body text-sm text-[#B91C1C]">{f.reason}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-[#1F2937]">{money(f.amount_cents, f.currency)}</td>
                  <td className="px-4 py-3 font-body text-xs text-[#6B7280]">#{f.attempt_count || 1}</td>
                  <td className="px-4 py-3">
                    {f.resolved
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-2 py-0.5 text-xs font-bold text-[#047857]"><CheckCircle2 size={12} /> Resolved</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-[#EF4444]/15 px-2 py-0.5 text-xs font-bold text-[#B91C1C]"><AlertTriangle size={12} /> Open</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      {f.hosted_invoice_url && (
                        <a href={f.hosted_invoice_url} target="_blank" rel="noreferrer" data-testid={`failure-invoice-${f.id}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-[#0D9488] hover:underline">
                          <ExternalLink size={12} /> Invoice
                        </a>
                      )}
                      {f.resolved ? (
                        <button onClick={() => resolve(f.id, false)} data-testid={`failure-reopen-${f.id}`}
                          className="inline-flex items-center gap-1 rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1 text-xs font-bold text-[#1F2937] hover:border-[#1F2937]">
                          Reopen
                        </button>
                      ) : (
                        <button onClick={() => resolve(f.id, true)} data-testid={`failure-resolve-${f.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[#10B981] px-3 py-1 text-xs font-bold text-white hover:bg-[#059669]">
                          <CheckCircle2 size={12} /> Resolve
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function POStatusPill({ status }) {
  const map = {
    submitted: { c: "#F59E0B", bg: "#F59E0B/10", label: "Submitted", icon: Clock },
    invoiced: { c: "#0D9488", bg: "#0D9488/10", label: "Invoiced", icon: Receipt },
    paid: { c: "#10B981", bg: "#10B981/10", label: "Paid", icon: CheckCircle2 },
    canceled: { c: "#6B7280", bg: "#6B7280/10", label: "Canceled", icon: XCircle },
  };
  const m = map[status] || map.submitted;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold bg-[${m.c}]/10 text-[${m.c}]`}
      style={{ backgroundColor: `${m.c}1A`, color: m.c }}
      data-testid={`po-status-${status}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

function PurchaseOrdersPanel() {
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [active, setActive] = useState(null); // po under review

  const load = async (status) => {
    setLoading(true);
    try {
      const d = await api.adminListPurchaseOrders(status || undefined);
      setPos(d.purchase_orders || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return pos;
    return pos.filter((p) => [p.organization, p.user_email, p.user_name, p.plan_name, p.po_number, p.invoice_number]
      .some((v) => (v || "").toString().toLowerCase().includes(t)));
  }, [pos, q]);

  const totals = useMemo(() => {
    const t = { submitted: 0, invoiced: 0, paid: 0, revenue: 0 };
    for (const p of pos) {
      t[p.status] = (t[p.status] || 0) + 1;
      if (p.status === "paid") t.revenue += (p.amount_cents || 0);
    }
    return t;
  }, [pos]);

  const doInvoice = async (po) => {
    const invNum = window.prompt("Invoice number (leave blank to auto-generate)", "") || "";
    const amtStr = window.prompt("Amount (USD, e.g. 45000.00)", ((po.amount_cents || 0) / 100).toFixed(2)) || "";
    const amount_cents = Math.round(parseFloat(amtStr || "0") * 100) || undefined;
    setBusyId(po.id);
    try {
      await api.adminInvoicePO(po.id, { invoice_number: invNum || undefined, amount_cents });
      toast.success("Marked as invoiced");
      load(statusFilter);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  const doMarkPaid = async (po) => {
    if (!window.confirm(`Mark PO for ${po.organization} as PAID? This will activate their plan (${po.seats} seats).`)) return;
    setBusyId(po.id);
    try {
      const r = await api.adminMarkPOPaid(po.id, { amount_cents: po.amount_cents });
      toast.success(r.provisioned ? `Plan activated & ${r.provisioned.seats} seats provisioned to their ${r.provisioned.org_type}` : "Plan activated");
      load(statusFilter);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  const doCancel = async (po) => {
    const reason = window.prompt("Cancellation reason (optional)", "") || "";
    if (!window.confirm(`Cancel PO for ${po.organization}?`)) return;
    setBusyId(po.id);
    try {
      await api.adminCancelPO(po.id, { notes: reason || undefined });
      toast.success("PO canceled");
      load(statusFilter);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="purchase-orders-card">
      <div className="flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><FileText size={20} /></div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">Purchase Orders</h2>
          <p className="font-body text-xs text-[#6B7280]">Review PO requests, mark them invoiced or paid to activate the buyer's plan.</p>
        </div>
        <button onClick={() => load(statusFilter)} data-testid="po-refresh" className="rounded-full border-2 border-[#E5DECF] px-3 py-1.5 text-sm font-bold text-[#1F2937] hover:border-[#0D9488]">
          <RefreshCcw size={14} className="inline-block mr-1" /> Refresh
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="po-stat-submitted"><div className="text-xs uppercase font-bold text-[#F59E0B]">Submitted</div><div className="font-heading text-2xl font-extrabold">{totals.submitted || 0}</div></div>
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="po-stat-invoiced"><div className="text-xs uppercase font-bold text-[#0D9488]">Invoiced</div><div className="font-heading text-2xl font-extrabold">{totals.invoiced || 0}</div></div>
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="po-stat-paid"><div className="text-xs uppercase font-bold text-[#10B981]">Paid</div><div className="font-heading text-2xl font-extrabold">{totals.paid || 0}</div></div>
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="po-stat-revenue"><div className="text-xs uppercase font-bold text-[#1F2937]">PO revenue</div><div className="font-heading text-2xl font-extrabold text-[#0D9488]">{money(totals.revenue)}</div></div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="po-search" className={`${inp} pl-9`} placeholder="Search org, buyer, plan, PO #" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="po-status-filter" className={inp + " max-w-[160px]"}>
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="invoiced">Invoiced</option>
          <option value="paid">Paid</option>
          <option value="canceled">Canceled</option>
        </select>
      </div>

      <div className="mt-4 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-[#7A3FF2]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center font-body text-sm text-[#6B7280]" data-testid="po-empty">
            No purchase orders yet. When a school or district submits one, it will land here.
          </div>
        ) : (
          <table className="w-full min-w-[820px] text-sm" data-testid="po-table">
            <thead>
              <tr className="text-left text-xs uppercase text-[#94A3B8]">
                <th className="py-2 pr-3">When</th>
                <th className="pr-3">Organization</th>
                <th className="pr-3">Buyer</th>
                <th className="pr-3">Plan · Seats</th>
                <th className="pr-3">Amount</th>
                <th className="pr-3">PO / Invoice #</th>
                <th className="pr-3">Status</th>
                <th className="pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-[#F2ECE1]" data-testid={`po-row-${p.id}`}>
                  <td className="py-3 pr-3 font-body text-xs text-[#6B7280]">{fmtDate(p.created_at)}</td>
                  <td className="pr-3 align-top">
                    <div className="flex items-center gap-1.5 font-bold text-[#1F2937]"><Building2 size={13} className="text-[#7A3FF2]" /> {p.organization}</div>
                    <div className="text-xs text-[#94A3B8] capitalize">{p.org_type}</div>
                  </td>
                  <td className="pr-3 align-top">
                    <div className="font-semibold text-[#1F2937]">{p.user_name || "—"}</div>
                    <div className="text-xs text-[#94A3B8]">{p.user_email || p.billing_email}</div>
                  </td>
                  <td className="pr-3 align-top">
                    <div className="font-semibold text-[#1F2937]">{p.plan_name}</div>
                    <div className="text-xs text-[#94A3B8]">{p.seats} seats</div>
                  </td>
                  <td className="pr-3 align-top font-mono text-sm">{money(p.amount_cents || p.estimated_amount_cents)}</td>
                  <td className="pr-3 align-top">
                    <div className="text-xs text-[#6B7280]">PO: {p.po_number || "—"}</div>
                    <div className="text-xs text-[#6B7280]">Inv: {p.invoice_number || "—"}</div>
                  </td>
                  <td className="pr-3 align-top"><POStatusPill status={p.status} /></td>
                  <td className="pr-3 align-top text-right">
                    <div className="inline-flex flex-wrap justify-end gap-1.5">
                      <button onClick={() => setActive(p)} data-testid={`po-view-${p.id}`} className="rounded-full border border-[#E5DECF] px-2.5 py-1 text-xs font-bold text-[#1F2937] hover:border-[#0D9488]">View</button>
                      {p.status === "submitted" && (
                        <button onClick={() => doInvoice(p)} disabled={busyId === p.id} data-testid={`po-invoice-${p.id}`} className="rounded-full bg-[#0D9488] px-2.5 py-1 text-xs font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">Invoice</button>
                      )}
                      {(p.status === "submitted" || p.status === "invoiced") && (
                        <button onClick={() => doMarkPaid(p)} disabled={busyId === p.id} data-testid={`po-mark-paid-${p.id}`} className="rounded-full bg-[#10B981] px-2.5 py-1 text-xs font-bold text-white hover:bg-[#059669] disabled:opacity-50">Mark paid</button>
                      )}
                      {p.status !== "paid" && p.status !== "canceled" && (
                        <button onClick={() => doCancel(p)} disabled={busyId === p.id} data-testid={`po-cancel-${p.id}`} className="rounded-full border border-[#E05A47] px-2.5 py-1 text-xs font-bold text-[#B4402F] hover:bg-[#E05A47]/10 disabled:opacity-50">Cancel</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-[70] flex justify-end bg-[#1F2937]/60" onClick={() => setActive(null)} data-testid="po-drawer">
          <div className="h-full w-full max-w-lg overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase font-bold text-[#7A3FF2]"><FileText size={14} /> Purchase order</div>
                <h3 className="mt-1 font-heading text-2xl font-extrabold text-[#1F2937]">{active.organization}</h3>
                <div className="mt-1"><POStatusPill status={active.status} /></div>
              </div>
              <button onClick={() => setActive(null)} data-testid="po-drawer-close" className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
            </div>
            <div className="mt-5 space-y-4 text-sm">
              <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-3">
                <div className="text-xs uppercase text-[#94A3B8]">Buyer</div>
                <div className="font-bold text-[#1F2937]">{active.user_name || "—"} <span className="text-xs text-[#6B7280] font-normal">({active.user_role})</span></div>
                <div className="text-xs text-[#6B7280]">{active.user_email}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs uppercase text-[#94A3B8]">Plan</div><div className="font-bold">{active.plan_name}</div></div>
                <div><div className="text-xs uppercase text-[#94A3B8]">Seats</div><div className="font-bold">{active.seats}</div></div>
                <div><div className="text-xs uppercase text-[#94A3B8]">Estimated</div><div className="font-mono">{money(active.estimated_amount_cents)}</div></div>
                <div><div className="text-xs uppercase text-[#94A3B8]">Amount</div><div className="font-mono">{money(active.amount_cents)}</div></div>
                <div><div className="text-xs uppercase text-[#94A3B8]">PO number</div><div>{active.po_number || "—"}</div></div>
                <div><div className="text-xs uppercase text-[#94A3B8]">Invoice</div><div>{active.invoice_number || "—"}</div></div>
              </div>
              <div className="rounded-2xl border border-[#E5DECF] p-3">
                <div className="text-xs uppercase text-[#94A3B8]">Billing contact</div>
                <div className="font-bold">{active.billing_name}</div>
                <div className="text-xs text-[#6B7280]">{active.billing_email}{active.billing_phone ? ` · ${active.billing_phone}` : ""}</div>
                {active.billing_address && <div className="mt-1 text-xs text-[#6B7280] whitespace-pre-line">{active.billing_address}</div>}
              </div>
              {active.notes && (
                <div className="rounded-2xl border border-[#E5DECF] p-3">
                  <div className="text-xs uppercase text-[#94A3B8]">Notes from buyer</div>
                  <div className="mt-1 whitespace-pre-line text-[#1F2937]">{active.notes}</div>
                </div>
              )}
              {(active.history || []).length > 0 && (
                <div className="rounded-2xl border border-[#E5DECF] p-3">
                  <div className="text-xs uppercase text-[#94A3B8] mb-1">History</div>
                  <ul className="space-y-0.5 text-xs">
                    {active.history.map((h, i) => (
                      <li key={i} className="text-[#6B7280]">{fmtDate(h.at)} — <span className="font-bold text-[#1F2937] capitalize">{h.action}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {active.provisioned && (
                <div className="rounded-2xl bg-[#10B981]/10 p-3 text-[#047857]">
                  <div className="flex items-center gap-1.5 font-bold"><CheckCircle2 size={14} /> Seats provisioned</div>
                  <div className="text-xs mt-0.5">{active.provisioned.seats} seats attached to the buyer's {active.provisioned.org_type}.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function CertificatesToggleCard() {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.getPlatformCertPref()
      .then((d) => setEnabled(!!d.enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const toggle = async () => {
    setSaving(true);
    try {
      const next = !enabled;
      await api.setPlatformCertPref(next);
      setEnabled(next);
      toast.success(next ? "Printable certificates enabled platform-wide" : "Certificate printing disabled platform-wide");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="certificates-card">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><FileText size={20} /></div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">Printable Certificates</h2>
          <p className="font-body text-xs text-[#6B7280]">Master switch for the "Print" button on every student's earned certificate. Certificates are always awarded — this only hides the print action.</p>
        </div>
        <button onClick={toggle} disabled={loading || saving} data-testid="platform-cert-toggle"
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-heading font-bold ${enabled ? "bg-[#0D9488] text-white hover:bg-[#0B7F74]" : "border-2 border-[#E5DECF] bg-white text-[#6B7280] hover:border-[#0D9488]"}`}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : (enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />)}
          {enabled ? "Enabled" : "Disabled"}
        </button>
      </div>
    </div>
  );
}



function ResellerSlugsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [orphanCount, setOrphanCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [query, setQuery] = useState("");
  const [onlyOrphaned, setOnlyOrphaned] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.adminListResellerSlugs();
      setRows(r.slugs || []);
      setTotal(r.total || 0);
      setOrphanCount(r.orphaned_count || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (onlyOrphaned && !r.orphaned) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (r.slug || "").toLowerCase().includes(q)
        || (r.name || "").toLowerCase().includes(q)
        || (r.owner?.email || "").toLowerCase().includes(q);
  });

  const purgeAll = async () => {
    if (orphanCount === 0) { toast.info("No orphaned slugs — nothing to purge."); return; }
    if (!window.confirm(`Delete ${orphanCount} orphaned brand slug${orphanCount === 1 ? "" : "s"}? Their unused license codes will be removed too. This can't be undone.`)) return;
    setPurging(true);
    try {
      const r = await api.adminPurgeResellerSlugs();
      toast.success(`Purged ${r.purged} slug${r.purged === 1 ? "" : "s"}${r.codes_purged ? ` · ${r.codes_purged} codes freed` : ""}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurging(false); }
  };

  const purgeOne = async (slug) => {
    if (!window.confirm(`Delete slug "${slug}"? Any unused codes tied to it will be removed. This can't be undone.`)) return;
    setDeleting((d) => ({ ...d, [slug]: true }));
    try {
      const r = await api.adminDeleteResellerSlug(slug);
      toast.success(`Slug "${slug}" freed${r.codes_purged ? ` · ${r.codes_purged} codes removed` : ""}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting((d) => ({ ...d, [slug]: false })); }
  };

  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="reseller-slugs-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Handshake size={18} className="text-[#0D9488]" />
          <h2 className="font-heading text-lg font-extrabold text-[#1F2937]">Reseller brand slugs</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="reseller-slugs-refresh" className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#E5DECF] px-3 py-1.5 text-xs font-bold text-[#6B7280] hover:border-[#0D9488] hover:text-[#0D9488]">
            <RefreshCcw size={13} /> Refresh
          </button>
          <button onClick={purgeAll} disabled={purging || orphanCount === 0} data-testid="reseller-slugs-purge"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#E05A47] px-4 py-1.5 text-xs font-heading font-bold text-white hover:brightness-95 disabled:opacity-50">
            {purging ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Purge orphans ({orphanCount})
          </button>
        </div>
      </div>
      <p className="mt-1 font-body text-sm text-[#6B7280]">
        Every reseller's public `/r/{"{slug}"}` URL. When a reseller account is deleted its slug becomes orphaned — purge orphans here so another partner can claim the same name.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] px-4 py-3">
          <div className="font-body text-[10px] uppercase tracking-wide font-semibold text-[#6B7280]">Total slugs</div>
          <div className="mt-0.5 font-heading text-2xl font-extrabold text-[#1F2937]" data-testid="slug-stat-total">{total}</div>
        </div>
        <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] px-4 py-3">
          <div className="font-body text-[10px] uppercase tracking-wide font-semibold text-[#6B7280]">Active</div>
          <div className="mt-0.5 font-heading text-2xl font-extrabold text-[#047857]" data-testid="slug-stat-active">{total - orphanCount}</div>
        </div>
        <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] px-4 py-3">
          <div className="font-body text-[10px] uppercase tracking-wide font-semibold text-[#6B7280]">Orphaned</div>
          <div className="mt-0.5 font-heading text-2xl font-extrabold text-[#E05A47]" data-testid="slug-stat-orphaned">{orphanCount}</div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-3 py-2 min-w-[220px]">
          <Search size={16} className="text-[#94A3B8]" />
          <input data-testid="reseller-slugs-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search slug, brand name or owner email…"
            className="w-full bg-transparent outline-none font-body text-sm text-[#1F2937]" />
        </div>
        <label className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1.5 text-xs font-bold text-[#6B7280]">
          <input type="checkbox" checked={onlyOrphaned} onChange={(e) => setOnlyOrphaned(e.target.checked)}
            data-testid="reseller-slugs-only-orphaned"
            className="h-4 w-4 rounded border-[#94A3B8] text-[#0D9488] focus:ring-[#0D9488]" />
          Orphaned only
        </label>
      </div>
      {loading ? (
        <div className="mt-6 flex justify-center"><Loader2 className="animate-spin text-[#0D9488]" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-[#E5DECF] bg-[#FAF7F2] p-6 text-center" data-testid="reseller-slugs-empty">
          <Sparkles size={22} className="mx-auto text-[#94A3B8]" />
          <p className="mt-2 font-body text-sm text-[#6B7280]">{rows.length === 0 ? "No reseller brands yet." : "No slugs match your filters."}</p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-2xl border border-[#E5DECF]" data-testid="reseller-slugs-table">
          <table className="w-full text-left">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wide text-[#6B7280]">
              <tr>
                <th className="px-4 py-2 font-bold">Slug / brand</th>
                <th className="px-4 py-2 font-bold">Owner</th>
                <th className="px-4 py-2 font-bold">Seats</th>
                <th className="px-4 py-2 font-bold">Status</th>
                <th className="px-4 py-2 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DECF] text-sm">
              {filtered.map((r) => (
                <tr key={r.slug} className={r.orphaned ? "bg-[#E05A47]/5" : "bg-white"} data-testid={`slug-row-${r.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ background: r.accent_color || "#0D9488" }} />
                      <code className="rounded-lg bg-[#F2ECE1] px-2 py-0.5 text-[11px] font-bold text-[#1F2937]">/r/{r.slug}</code>
                    </div>
                    <div className="mt-0.5 font-body text-xs text-[#6B7280]">{r.name}</div>
                  </td>
                  <td className="px-4 py-3 font-body text-xs text-[#6B7280]">
                    {r.owner ? (
                      <>
                        <div className="font-bold text-[#1F2937]">{r.owner.name}</div>
                        <div>{r.owner.email}</div>
                      </>
                    ) : (
                      <span className="italic text-[#94A3B8]">Account deleted</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-body text-xs text-[#6B7280]">
                    <span className="font-bold text-[#1F2937]">{r.redeemed_seats}</span> redeemed
                  </td>
                  <td className="px-4 py-3">
                    {r.orphaned ? (
                      <span className="rounded-full bg-[#E05A47]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B4402F]" data-testid={`slug-status-orphaned-${r.slug}`}>Orphaned</span>
                    ) : (
                      <span className="rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#047857]" data-testid={`slug-status-active-${r.slug}`}>Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => purgeOne(r.slug)}
                      disabled={!r.orphaned || deleting[r.slug]}
                      data-testid={`delete-slug-${r.slug}`}
                      title={r.orphaned ? "Free this slug" : "Delete the reseller account first"}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-[#E05A47] px-3 py-1.5 text-xs font-heading font-bold text-[#E05A47] hover:bg-[#E05A47] hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-[#E05A47]">
                      {deleting[r.slug] ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AIKeysCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState({ openai: {}, gemini: {} });
  const [openaiKey, setOpenaiKey] = useState("");
  const [geminiKey, setGeminiKey] = useState("");
  const [showO, setShowO] = useState(false);
  const [showG, setShowG] = useState(false);
  const [openaiModel, setOpenaiModel] = useState("");
  const [geminiModel, setGeminiModel] = useState("");
  const [provider, setProvider] = useState("auto");

  const load = async () => {
    try {
      const d = await api.getAIKeys();
      setInfo(d);
      setOpenaiModel(d.openai?.model || ""); setGeminiModel(d.gemini?.model || ""); setProvider(d.lesson_ai?.preference || "auto");
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { openai_model: openaiModel, gemini_model: geminiModel, lesson_provider: provider };
      if (openaiKey !== "") payload.openai_key = openaiKey;
      if (geminiKey !== "") payload.gemini_key = geminiKey;
      await api.saveAIKeys(payload);
      toast.success("AI settings saved");
      setOpenaiKey(""); setGeminiKey("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const clearKey = async (which) => {
    if (!window.confirm(`Clear the custom ${which === "openai_key" ? "OpenAI" : "Gemini"} key and fall back to the platform default?`)) return;
    setSaving(true);
    try {
      await api.saveAIKeys({ [which]: "" });
      toast.success("Key cleared");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="ai-keys-card">
      <div className="flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><Volume2 size={20} /></div>
        <div>
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">AI Keys (BYOK)</h2>
          <p className="font-body text-xs text-[#6B7280]">Add your own OpenAI &amp; Gemini keys. They power TTS <em>and</em> every AI lesson generation (Studio prompts, bulk upload, Differentiate, remediation, tutorials) instead of the Emergent Universal Key.</p>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border-2 border-[#0D9488]/30 bg-[#0D9488]/5 p-4" data-testid="lesson-ai-card">
        <div className="text-xs font-bold uppercase tracking-widest text-[#0D9488]">Lesson generation uses</div>
        <div className="mt-1 font-heading text-base font-extrabold text-[#1F2937]" data-testid="lesson-ai-active">{info.lesson_ai?.label} · <span className="font-mono text-sm">{info.lesson_ai?.model}</span></div>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <label className="text-xs font-bold text-[#6B7280]">Provider
            <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="lesson-provider-select" className="mt-1 w-full rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-sm text-[#1F2937]">
              <option value="auto">Auto — my OpenAI key, else my Gemini key, else Emergent</option>
              <option value="openai">Always OpenAI (needs my key)</option>
              <option value="gemini">Always Gemini (needs my key)</option>
              <option value="emergent">Always Emergent Universal Key</option>
            </select>
          </label>
          <label className="text-xs font-bold text-[#6B7280]">OpenAI model
            <input value={openaiModel} onChange={(e) => setOpenaiModel(e.target.value)} data-testid="openai-model-input" placeholder="gpt-5.4" className="mt-1 w-full rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-mono text-sm text-[#1F2937]" />
          </label>
          <label className="text-xs font-bold text-[#6B7280]">Gemini model
            <input value={geminiModel} onChange={(e) => setGeminiModel(e.target.value)} data-testid="gemini-model-input" placeholder="gemini-flash-latest" className="mt-1 w-full rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-mono text-sm text-[#1F2937]" />
          </label>
        </div>
        <p className="mt-2 font-body text-[11px] text-[#94A3B8]">A provider is only used when its key is saved below; otherwise generation falls back to the Emergent Universal Key. Save to apply.</p>
      </div>

      <div className="mt-4 rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4">
        <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${info.openai?.has_custom ? "bg-[#10B981]/10 text-[#047857]" : "bg-white text-[#6B7280]"}`} data-testid="openai-status">
          <ShieldCheck size={16} />
          {info.openai?.has_custom
            ? `Custom OpenAI key active (${info.openai.hint}). Leave blank to keep the current one.`
            : `Using the platform default (${info.openai?.hint || "not set"}).`}
        </div>
        <label className="mt-3 block text-sm font-bold text-[#1F2937]">OpenAI API key</label>
        <div className="relative mt-1">
          <input
            type={showO ? "text" : "password"}
            value={openaiKey}
            onChange={(e) => setOpenaiKey(e.target.value)}
            data-testid="openai-key-input"
            className={`${inp} pr-11`}
            placeholder="sk-..."
            autoComplete="off"
          />
          <button type="button" onClick={() => setShowO((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0D9488]" aria-label="Toggle key visibility">
            {showO ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {info.openai?.has_custom && (
          <button onClick={() => clearKey("openai_key")} data-testid="openai-clear" className="mt-2 text-xs font-bold text-[#E05A47] hover:underline">Clear custom OpenAI key</button>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4">
        <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${info.gemini?.has_custom ? "bg-[#10B981]/10 text-[#047857]" : "bg-white text-[#6B7280]"}`} data-testid="gemini-status">
          <ShieldCheck size={16} />
          {info.gemini?.has_custom
            ? `Custom Gemini key active (${info.gemini.hint}). Leave blank to keep the current one.`
            : info.gemini?.hint
              ? `Env fallback active (${info.gemini.hint}).`
              : "No Gemini key configured — Gemini voices are hidden until you add one."}
        </div>
        <label className="mt-3 block text-sm font-bold text-[#1F2937]">Gemini API key (Google AI Studio)</label>
        <div className="relative mt-1">
          <input
            type={showG ? "text" : "password"}
            value={geminiKey}
            onChange={(e) => setGeminiKey(e.target.value)}
            data-testid="gemini-key-input"
            className={`${inp} pr-11`}
            placeholder="AIza..."
            autoComplete="off"
          />
          <button type="button" onClick={() => setShowG((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0D9488]" aria-label="Toggle key visibility">
            {showG ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <p className="mt-2 font-body text-xs text-[#94A3B8]">
          Get one at <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="font-bold text-[#0D9488] hover:underline">aistudio.google.com/app/apikey</a>.
        </p>
        {info.gemini?.has_custom && (
          <button onClick={() => clearKey("gemini_key")} data-testid="gemini-clear" className="mt-2 text-xs font-bold text-[#E05A47] hover:underline">Clear custom Gemini key</button>
        )}
      </div>

      <button onClick={save} disabled={saving} data-testid="ai-keys-save" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FF9E1B] px-6 py-3 font-heading font-bold text-[#1F2937] hover:brightness-95 disabled:opacity-50">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save AI settings
      </button>
    </div>
  );
}

function BulkUploadCard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const d = await api.bulkRecentJobs(); setJobs(d.jobs || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const undoJob = async (jobId, ok) => {
    if (!window.confirm(`Delete every lesson this batch created (${ok})?`)) return;
    try {
      const { deleted } = await api.bulkJobUndo(jobId);
      toast.success(`Deleted ${deleted} lessons`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="bulk-upload-card">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FF9E1B]/15 text-[#B45309]"><Upload size={20} /></div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">Bulk lesson upload</h2>
          <p className="font-body text-xs text-[#6B7280]">
            Paste a batch of prompts (or upload a CSV) and turn them into global lessons in one shot. Each row can be generated with AI or created as a title-only stub for later editing.
          </p>
        </div>
        <Link to="/admin/bulk-upload" data-testid="bulk-upload-open" className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] px-4 py-2 font-heading text-sm font-bold text-white hover:bg-[#0B7F74]">
          <Upload size={14} /> Open
        </Link>
      </div>

      {/* Recent jobs list */}
      <div className="mt-4" data-testid="bulk-recent-jobs">
        <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-[#6B7280]">
          <Clock size={12} className="text-[#0D9488]" /> Recent uploads
          {jobs.some((j) => j.status === "running") && (
            <button
              onClick={async () => {
                if (!window.confirm("Reset every job that's still marked 'running'? Use this only if a job has been stuck for more than a minute.")) return;
                try {
                  const r = await api.bulkReapStale();
                  toast.success(`Reset ${r.bulk_reaped} bulk + ${r.ai_reaped} AI job(s). You can re-run the remaining rows now.`);
                  loadJobs();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="bulk-reap-stale-btn"
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#E05A47]/10 px-2.5 py-1 text-[11px] font-bold text-[#B23125] hover:bg-[#E05A47]/20"
              title="Force-fail any bulk / AI job that's still marked 'running'"
            >
              <RotateCcw size={11} /> Reset stuck jobs
            </button>
          )}
        </div>
        {loading ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-[#94A3B8]"><Loader2 size={12} className="animate-spin" /> Loading…</div>
        ) : jobs.length === 0 ? (
          <p className="mt-2 font-body text-xs text-[#94A3B8]">You haven't run a bulk upload yet — click "Open" above to try one.</p>
        ) : (
          <ul className="mt-2 divide-y divide-[#F2ECE1] rounded-2xl border border-[#E5DECF]">
            {jobs.map((j) => {
              const isDone = j.status === "done";
              const isUndone = j.status === "undone";
              const isCancelled = j.status === "cancelled";
              return (
                <li key={j.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`bulk-job-${j.id}`}>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                    j.status === "running" ? "bg-[#F59E0B]/15 text-[#B45309]" :
                    isUndone ? "bg-[#94A3B8]/15 text-[#475569]" :
                    isCancelled ? "bg-[#E05A47]/15 text-[#B23125]" :
                    "bg-[#10B981]/15 text-[#047857]"
                  }`}>{j.status}</span>
                  <span className="font-mono text-xs text-[#6B7280]">{j.id.slice(0, 8)}</span>
                  <span className="font-body text-xs text-[#6B7280]">{fmtTime(j.created_at)}</span>
                  <span className="font-body text-xs font-semibold text-[#1F2937]">
                    {j.processed}/{j.total} · <span className="text-[#047857]">{j.ok} ok</span>{j.failed > 0 && <> · <span className="text-[#B23125]">{j.failed} failed</span></>}
                    {j.generate === false && <span className="ml-2 rounded-full bg-[#F2ECE1] px-2 py-0.5 text-[10px] text-[#6B7280]">stubs</span>}
                  </span>
                  <div className="ml-auto flex flex-wrap items-center gap-1.5">
                    <Link to={`/admin/bulk-upload?job=${j.id}`} data-testid={`bulk-job-open-${j.id}`} className="inline-flex items-center gap-1 rounded-full border border-[#E5DECF] bg-white px-2.5 py-1 text-[11px] font-bold text-[#0D9488] hover:bg-[#17BEBB]/10">
                      <ExternalLink size={11} /> Open
                    </Link>
                    {(isDone || isCancelled) && j.ok > 0 && (
                      <button onClick={() => undoJob(j.id, j.ok)} data-testid={`bulk-job-undo-${j.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#E05A47]/10 px-2.5 py-1 text-[11px] font-bold text-[#B23125] hover:bg-[#E05A47]/20">
                        <Trash2 size={11} /> Undo
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState({ has_custom: false, secret_hint: null, publishable_key: "" });
  const [secret, setSecret] = useState("");
  const [publishable, setPublishable] = useState("");
  const [showSecret, setShowSecret] = useState(false);

  const load = async () => {
    try {
      const d = await api.getStripeSettings();
      setInfo(d);
      setPublishable(d.publishable_key || "");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveStripeSettings({ secret_key: secret || null, publishable_key: publishable });
      toast.success("Stripe keys saved & applied");
      setSecret("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Platform Admin</div>
      <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">Settings</h1>
      <p className="mt-1 font-body text-[#6B7280]">Configure billing, view every payment and see who's subscribed.</p>

      <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="stripe-keys-card">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#0D9488]/10 text-[#0D9488]"><KeyRound size={20} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-[#1F2937]">Stripe API keys</h2>
            <p className="font-body text-xs text-[#6B7280]">Swap in your own live Stripe account without a redeploy.</p>
          </div>
        </div>

        <div className={`mt-4 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${info.has_custom ? "bg-[#10B981]/10 text-[#047857]" : "bg-[#F2ECE1] text-[#6B7280]"}`} data-testid="stripe-status">
          <ShieldCheck size={16} />
          {info.has_custom ? `Custom keys are active (current: ${info.secret_hint}). Leave secret blank to keep the current one.` : `Using the platform default keys${info.secret_hint ? ` (current: ${info.secret_hint})` : ""}.`}
        </div>

        <label className="mt-4 block text-sm font-bold text-[#1F2937]">Secret key</label>
        <div className="relative mt-1">
          <input
            type={showSecret ? "text" : "password"}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid="stripe-secret-input"
            className={`${inp} pr-11`}
            placeholder="sk_live... or sk_test..."
            autoComplete="off"
          />
          <button type="button" onClick={() => setShowSecret((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0D9488]" aria-label="Toggle secret visibility">
            {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <label className="mt-4 block text-sm font-bold text-[#1F2937]">Publishable key</label>
        <input
          value={publishable}
          onChange={(e) => setPublishable(e.target.value)}
          data-testid="stripe-publishable-input"
          className={`${inp} mt-1`}
          placeholder="pk_live... or pk_test..."
          autoComplete="off"
        />

        <button onClick={save} disabled={saving} data-testid="stripe-save" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FF9E1B] px-6 py-3 font-heading font-bold text-[#1F2937] hover:brightness-95 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save keys
        </button>
      </div>

      <PaymentsPanel />
      <PurchaseOrdersPanel />
      <ResellerSlugsPanel />
      <AIKeysCard />
      <BulkUploadCard />
      <CertificatesToggleCard />
      <PaymentAlertsPanel />
    </div>
  );
}
